import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import qs from 'query-string'
import { TablePagination, useMediaQuery } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import { Container, ContentHeader, LoadingFeedback, Page } from 'components'
import { DocumentEditForm, DocumentTabs } from './components'

import helpers from 'helpers'
import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'
import usePermissions from 'hooks/usePermissions'
import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import { routes } from 'Routes'
import * as service from 'service'
import constants from 'constants/index'

const {
  ACCEPTED_DOCUMENTS_CATEGORY_ID,
  ACTIVE_STATUS_ID,
  APPROVAL_DOCUMENTS_CATEGORY_ID,
  DOCUMENTS_CATEGORY_ID,
  WRITTEN_CONTENT_KIND,
} = constants.documents

const DocumentsEdit = ({ match }) => {
  const { documentId } = match?.params
  const history = useHistory()
  const location = useLocation()
  const search = qs.parse(location.search)

  const { CONSENTS } = constants.permissions

  const [hasPermission, setHasPermission] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentTab, setCurrentTab] = useState(DOCUMENTS_CATEGORY_ID)

  const permissions = usePermissions()
  const { user } = useAuth()
  const snackbar = useSnackbar()

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const {
    response,
    isLoading,
    refresh: refreshPrivacyPolicy,
  } = useFetch(
    service.dponet.privacyPolicies.get,
    { privacyPolicyId: documentId },
    [documentId],
  )

  const privacyPolicy = response?.data?.privacyPolicy
  const consentFormId = privacyPolicy?.consentFormId
  const hasCollectApproval = privacyPolicy?.collectApproval
  const expirationConfigs =
    response?.data?.privacyPolicy?.privacyPolicyExpirationConfig || {}

  const { response: consentFormResponse, isLoading: consentFormIsLoading } =
    useFetch(
      service.dponet.consent.get,
      { consentFormId },
      [consentFormId, hasPermission],
      !!consentFormId && hasPermission,
    )

  const refresh = () => {
    refreshPrivacyPolicy()
    refreshDocumentVersions()
  }

  const {
    response: privacyPermissionsResponse,
    loading: permissionsIsLoading,
  } = useFetch(
    service.dponet.privacyPolicies.getPrivacyPermissions,
    { privacyPolicyId: documentId },
    [documentId],
    !!documentId,
  )

  const { response: usersResponse, Loading: usersIsLoading } = useFetch(
    service.dponet.departments.availableUsers,
    {},
  )

  const { response: departmentsResponse, loading: departmentsIsLoading } =
    useFetch(
      service.dponet.departments.getAll,
      { perPage: 1000, active: true, order: 'departments.name ASC' },
      [],
    )

  const {
    page: documentVersionsPage,
    perPage: documentVersionsPerPage,
    handleChangePage: handleChangeDocumentVersionsPage,
    handleChangeRowsPerPage: handleChangeDocumentVersionsRowsPerPage,
  } = usePagination({ localState: true, initialPerPage: 5 })

  const {
    response: documentVersionsResponse,
    isLoading: documentVersionsIsLoading,
    refresh: refreshDocumentVersions,
  } = useFetch(
    service.dponet.privacyPolicies.listVersions,
    {
      privacyPolicyId: documentId,
      minimal: true,
      page: documentVersionsPage,
      perPage: documentVersionsPerPage,
    },
    [documentId, documentVersionsPage, documentVersionsPerPage],
    !!documentId,
  )

  const documentVersions =
    documentVersionsResponse?.data?.documentVersions || []

  const {
    page: linkedDocumentsPage,
    perPage: linkedDocumentsPerPage,
    handleChangePage: handleChangeLinkedDocumentsPage,
    handleChangeRowsPerPage: handleChangeLinkedDocumentsRowsPerPage,
  } = usePagination({ localState: true, initialPerPage: 5 })

  const {
    response: linkedDocumentsResponse,
    isLoading: linkedDocumentsIsLoading,
    refresh: refreshLinkedDocuments,
  } = useFetch(
    service.dponet.privacyPolicies.listLinkedDocuments,
    {
      privacyPolicyId: documentId,
      page: linkedDocumentsPage,
      perPage: linkedDocumentsPerPage,
    },
    [documentId, linkedDocumentsPage, linkedDocumentsPerPage],
    !!documentId,
  )

  const linkedDocuments = linkedDocumentsResponse?.data?.linkedDocuments || []

  const consentForm = consentFormResponse?.data?.consentForm
  const hasCollect = privacyPolicy?.collectAccepted
  const isLoadingResponses =
    isLoading || consentFormIsLoading || documentVersionsIsLoading
  const documentInactive = privacyPolicy?.status !== ACTIVE_STATUS_ID
  const documentWithoutVersion = isEmpty(documentVersions)
  const blocksConsentFormCreation =
    !consentFormId && (documentInactive || documentWithoutVersion)
  const disabledAcceptedTab =
    !hasCollect || !hasPermission || blocksConsentFormCreation
  const enableAcceptedTab =
    hasCollect && hasPermission && !blocksConsentFormCreation

  const disabledAcceptedTooltip =
    {
      [!consentFormId && documentWithoutVersion]:
        'Não é possível criar o formulário de consentimento pois o documento ainda não possui uma versão criada.',
      [!consentFormId && documentInactive]:
        'Não é possível criar o formulário de consentimento pois o documento não está ativo.',
      [!hasCollect]:
        'Você não pode modificar os aceites se não estiver coletando!',
      [!hasPermission]:
        'Você não possui permissões suficientes para editar e visualizar os consentimentos. Caso precise de acesso, entre em contato com um superior da empresa.',
    }[true] ?? ''

  const users = usersResponse?.data?.users || []
  const availableUsers = users.filter((u) => u.id !== user.id)
  const departments = departmentsResponse?.data?.departments || []

  const isWrittenContent = privacyPolicy?.contentKind === WRITTEN_CONTENT_KIND

  const handleBackRedirection = () => history.push(routes.documents.all)

  const downloadDocument = async (documentVersionId) => {
    try {
      setLoading(true)

      const response = await service.dponet.documentVersions.exportDocument({
        documentVersionId,
      })

      const file = await helpers.file.fromUrl(
        response?.data?.fileUrl,
        `documento-versao-${documentVersionId}.pdf`,
      )

      helpers.file.handleDownload(file)

      snackbar.open({
        message: constants.documents.DOWNLOAD_SNACKBAR_MESSAGE.document,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (
      permissions.permitted(CONSENTS.LIST) &&
      permissions.permitted(CONSENTS.CREATE) &&
      permissions.permitted(CONSENTS.SHOW)
    )
      setHasPermission(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const tabFromUrl = urlParams.get('tab')

    if (tabFromUrl === ACCEPTED_DOCUMENTS_CATEGORY_ID)
      setCurrentTab(ACCEPTED_DOCUMENTS_CATEGORY_ID)
    if (tabFromUrl === APPROVAL_DOCUMENTS_CATEGORY_ID)
      setCurrentTab(APPROVAL_DOCUMENTS_CATEGORY_ID)
  }, [location.search])

  useEffect(() => {
    if (privacyPolicy && !isLoadingResponses) {
      const searchTab = search?.tab

      if (
        privacyPolicy?.collectApproval &&
        searchTab === APPROVAL_DOCUMENTS_CATEGORY_ID
      ) {
        setCurrentTab(APPROVAL_DOCUMENTS_CATEGORY_ID)
      } else {
        if (enableAcceptedTab && searchTab === ACCEPTED_DOCUMENTS_CATEGORY_ID) {
          setCurrentTab(ACCEPTED_DOCUMENTS_CATEGORY_ID)
        } else {
          setCurrentTab(DOCUMENTS_CATEGORY_ID)
        }
      }

      const searchParams = { ...search }
      delete searchParams.tab
      history.replace(`${location.pathname}?${qs.stringify(searchParams)}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingResponses, hasPermission])

  const loadingContent =
    isLoading || consentFormIsLoading || documentVersionsIsLoading || loading

  const versionTablePagination = (
    <TablePagination
      component="div"
      count={documentVersionsResponse?.data?.meta?.totalCount || 0}
      onPageChange={handleChangeDocumentVersionsPage}
      onRowsPerPageChange={handleChangeDocumentVersionsRowsPerPage}
      page={documentVersionsPage - 1}
      rowsPerPage={documentVersionsPerPage}
      rowsPerPageOptions={[5, 10, 25, 100]}
      labelRowsPerPage={isDesktop ? 'Por página' : ''}
      nextIconButtonProps={{ size: 'small' }}
      backIconButtonProps={{ size: 'small' }}
    />
  )

  return (
    <Page title="Editar documento">
      <Container maxWidth={false}>
        <LoadingFeedback open={loadingContent} />
        <ContentHeader title="Editar documento" />
        <DocumentTabs
          currentTab={currentTab}
          onChange={(_, tab) => setCurrentTab(tab)}
          showSettingsTab
          showAcceptedTab={isWrittenContent}
          showApprovalTab={isWrittenContent}
          showControlsTab={isWrittenContent}
          showHistoryTab={isWrittenContent}
          disabledAcceptedTab={disabledAcceptedTab}
          disabledAcceptedTooltip={disabledAcceptedTooltip}
          disabledApprovalTab={!hasCollectApproval}
        />
        {!isLoading ? (
          <DocumentEditForm
            expirationConfigs={expirationConfigs}
            availableUsers={availableUsers}
            consentForm={consentForm}
            currentDocument={privacyPolicy}
            currentTab={currentTab}
            departments={departments}
            departmentsIsLoading={departmentsIsLoading}
            documentId={documentId}
            handleBackRedirection={handleBackRedirection}
            isLoadingResponses={isLoadingResponses}
            permissionsIsLoading={permissionsIsLoading}
            privacyPermissionsResponse={privacyPermissionsResponse}
            refresh={refresh}
            setCurrentTab={setCurrentTab}
            setLoading={setLoading}
            usersIsLoading={usersIsLoading}
            versions={{
              documentVersions,
              isLoading: documentVersionsIsLoading,
              downloadDocument,
              pagination: versionTablePagination,
            }}
            controls={{
              linkedDocuments,
              isLoading: linkedDocumentsIsLoading,
              refresh: refreshLinkedDocuments,
              pagination: {
                page: linkedDocumentsPage,
                perPage: linkedDocumentsPerPage,
                totalCount: linkedDocumentsResponse?.data?.meta?.totalCount,
                onPageChange: handleChangeLinkedDocumentsPage,
                onRowsPerPageChange: handleChangeLinkedDocumentsRowsPerPage,
              },
            }}
          />
        ) : (
          <Skeleton variant="rect" width="100%" height={400} />
        )}
      </Container>
    </Page>
  )
}

export default DocumentsEdit
